import type {
  League,
  LeagueSquad,
  SquadPlayer,
  TransferHistoryEntry,
} from '../types/database';
import type { PlayerPoolEntry } from './tennisTransfers';

/**
 * Pure helpers backing the AdminTennisTransferPanel (admin-on-behalf transfer).
 *
 * Design constraint: the actual swap math is owned by ./tennisTransfers.ts and
 * is already covered by an extensive test suite. These helpers exist purely to
 * (a) drive the admin dropdowns and (b) shape the history entry so the
 * admin-acted flag is captured. They MUST NOT duplicate any transfer logic.
 */

// Default cap when a league hasn't overridden tennisSquadRules.maxTransfers.
const DEFAULT_MAX_TRANSFERS = 4;

export interface AdminSquadOption {
  squad: LeagueSquad;
  /** maxTransfers - transfersUsed; Infinity when the league is uncapped. */
  transfersLeft: number;
  /** True when transfersLeft <= 0 and the league has a finite cap. */
  isDisabled: boolean;
  /** Dropdown-ready label: "{squadName} — {N} left" / "{squadName} — 0 left". */
  displayName: string;
}

export function getTennisLeagueOptionsForAdminTransfer(leagues: League[]): League[] {
  return leagues
    .filter((l) => l && l.sport === 'tennis')
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

export function getTennisSquadOptionsForAdminTransfer(
  squads: LeagueSquad[],
  league: League,
): AdminSquadOption[] {
  const rawMax = league?.tennisSquadRules?.maxTransfers;
  const maxTransfers = rawMax === undefined ? DEFAULT_MAX_TRANSFERS : rawMax;
  const uncapped = maxTransfers <= 0;

  const options: AdminSquadOption[] = squads.map((squad) => {
    const used = Number(squad.transfersUsed ?? 0);
    const transfersLeft = uncapped ? Infinity : maxTransfers - used;
    const isDisabled = !uncapped && transfersLeft <= 0;
    const leftLabel = uncapped ? '∞' : `${Math.max(0, transfersLeft)}`;
    const displayName = `${squad.squadName} — ${leftLabel} left`;
    return { squad, transfersLeft, isDisabled, displayName };
  });

  options.sort((a, b) =>
    (a.squad.squadName || '').localeCompare(b.squad.squadName || ''),
  );

  return options;
}

interface BuildHistoryEntryBase {
  bankedDelta: number;
  previousBanked: number;
  pointsBefore: number;
  /** When set, marks the entry as admin-initiated on behalf of the squad owner. */
  actedByAdminUid?: string;
}

interface BuildPlayerSwapHistory extends BuildHistoryEntryBase {
  transferType: 'playerSwap';
  gender: 'men' | 'women';
  outgoingPlayer: SquadPlayer | null | undefined;
  incomingPlayer: PlayerPoolEntry;
}

interface BuildPositionSwapHistory extends BuildHistoryEntryBase {
  transferType: 'positionSwap';
  gender: 'men' | 'women';
  slotA: number;
  slotB: number;
}

export type BuildTennisTransferHistoryEntryParams =
  | BuildPlayerSwapHistory
  | BuildPositionSwapHistory;

export function buildTennisTransferHistoryEntry(
  params: BuildTennisTransferHistoryEntryParams,
): TransferHistoryEntry {
  const totalBankedAfter = params.previousBanked + params.bankedDelta;

  const base: TransferHistoryEntry = {
    timestamp: new Date(),
    transferType:
      params.transferType === 'playerSwap'
        ? 'tennisPlayerSwap'
        : 'tennisPositionSwap',
    changeType:
      params.transferType === 'playerSwap'
        ? 'playerSubstitution'
        : 'roleReassignment',
    tennisGender: params.gender,
    bankedAmount: params.bankedDelta,
    totalBankedAfter,
    pointsBefore: params.pointsBefore,
    pointsAfter: params.pointsBefore, // unchanged — delta is banked, not gained
  };

  if (params.transferType === 'playerSwap') {
    base.playerOut = params.outgoingPlayer?.playerId;
    base.playerIn = params.incomingPlayer.playerId;
  } else {
    base.tennisSlotA = params.slotA;
    base.tennisSlotB = params.slotB;
  }

  if (params.actedByAdminUid !== undefined) {
    return { ...base, actedByAdminUid: params.actedByAdminUid };
  }
  return base;
}
